import * as Sentry from '@sentry/react';
import { AxiosError, AxiosRequestTransformer } from 'axios';
import { User } from 'oidc-client-ts';
import { Api as AddressBookApi } from '~api/address_book';
import { Api as AuthApi } from '~api/auth';
import { Api as CarrierContractsApi } from '~api/carrier_contracts';
import { Api as ConfigApi } from '~api/config';
import { Api as DashboardApi } from '~api/dashboard';
import { Api as GentokenApi } from '~api/gentoken';
import { Api as LabelMergerApi } from '~api/labelmerger';
import { Api as ReportApi } from '~api/reports';
import { Api as SelfcareApi } from '~api/selfcare';
import { Api as SomApi } from '~api/som';
import { Api as SomSearchApi } from '~api/som_search';
import { Api as TosApi } from '~api/tos';
import { Api as UserProfileApi } from '~api/userprofile';
import { Api as WimoApi } from '~api/wimo';

import { AppAuthConfig, AppFetchConfig } from '~src/main/config';

import { getStoredSiteId } from './hooks/useSiteId';

/**
 * Axios request transformer to attach bearer token to each request
 *
 * @external https://github.com/authts/react-oidc-context?tab=readme-ov-file#call-a-protected-api
 */
const AuthTransformer: AxiosRequestTransformer = (data, headers) => {
  const oidcStorage = localStorage.getItem(`oidc.user:${AppAuthConfig.authority}:${AppAuthConfig.client_id}`);

  if (!oidcStorage) {
    return JSON.stringify(data);
  }

  const user = User.fromStorageString(oidcStorage);

  headers.Authorization = `Bearer ${user.access_token}`;
  headers['X-Site-Id'] = getStoredSiteId();

  return JSON.stringify(data);
};

export const AddressBookApiInstance = new AddressBookApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/address_book/`,
  transformRequest: [AuthTransformer],
});

export const CarrierContractsApiInstance = new CarrierContractsApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/carrier_contracts/`,
  transformRequest: [AuthTransformer],
});

export const TosApiInstance = new TosApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/tos/`,
  transformRequest: [AuthTransformer],
});

export const SomSearchApiInstance = new SomSearchApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/som_search/`,
  transformRequest: [AuthTransformer],
});

export const SomApiInstance = new SomApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/som/`,
  transformRequest: [AuthTransformer],
});

export const ConfigApiInstance = new ConfigApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/config`,
  transformRequest: [AuthTransformer],
});

export const UserProfileApiInstance = new UserProfileApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/userprofile`,
  transformRequest: [AuthTransformer],
});

export const SelfcareApiInstance = new SelfcareApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/selfcare/`,
  transformRequest: [AuthTransformer],
});

export const WimoApiInstance = new WimoApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/wimo/`,
  transformRequest: [AuthTransformer],
});

export const GentokenApiInstance = new GentokenApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/gentoken/`,
  transformRequest: [AuthTransformer],
});

export const LabelMergerApiInstance = new LabelMergerApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/label_merger/`,
  transformRequest: [AuthTransformer],
});

export const DashboardApiInstance = new DashboardApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/dashboard`,
  transformRequest: [AuthTransformer],
});

export const ReportApiInstance = new ReportApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/frontend/reports/`,
  transformRequest: [AuthTransformer],
});

export const AuthApiInstance = new AuthApi({
  baseURL: `${AppFetchConfig.apiUrl}/v1/auth/`,
  transformRequest: [AuthTransformer],
});

function globalErrorHandler(error: AxiosError) {
  if (error.response?.status === 400 || error.response?.status === 401) {
    window.location.href = '/expired';
  }

  return Promise.reject(error);
}

function sentryContextHandler(error: AxiosError<{ trace_id?: string }>) {
  if (error.response?.data.trace_id) {
    Sentry.setTag('ingrid.trace_id', error.response.data.trace_id);
  }

  return Promise.reject(error);
}

AuthApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
AddressBookApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
CarrierContractsApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
TosApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
SomSearchApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
SomApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
ConfigApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
UserProfileApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
SelfcareApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
WimoApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
GentokenApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
LabelMergerApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
DashboardApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);
ReportApiInstance.instance.interceptors.response.use(res => res, sentryContextHandler);

AuthApiInstance.instance.interceptors.response.use(res => res, globalErrorHandler);
